import { memo } from "react";
// MUI
import { Divider, Grid, Stack } from "@mui/material";

// Components
import CollapseArea from "@/Layouts/components/Basics/CollapseArea";
import AdministrationToolsLoader from "./AdministrationToolsLoader.tsx";

// Sections
import GeneralLedgerPrefix from "./GeneralLedgerPrefix";
import Sources from "./Sources";
import CITControlTypes from "./CITControlTypes";
import ReputationManagementSelection from "./ReputationManagementSelection";
import OpportunityMining from "./OpportunityMining";
import CommissionCycleLastUpdate from "./CommissionCycleLastUpdate";
import DealerManagementSystemProvider from "./DealerManagementSystem/DealerManagementSystemProvider";
import PhoneServiceProvider from "./PhoneServiceProvider";
import GlobalPositioningService from "./GlobalPositioningService/GlobalPositioningService.tsx";
import KeyManagementSystem from "./KeyManagementSystem/KeyManagementSystem";
import PayrollProvider from "./PayrollProvider";
import { IAdministrationTools } from "@Settings/types/administrationTools/model";
import InventoryMapping from "@Settings/components/AdministrationTools/PublishInventory";
import DealerTradeAmountValues from "./DealerTradeAmountValues/index.tsx";
import DealerManagementDataSource from "@Settings/components/AdministrationTools/DealerManagementSystem/DealerManagementDataSource/index.tsx";
import DealerManagementSystemIds from "@Settings/components/AdministrationTools/DealerManagementSystem/DealerManagementSystemIds/index.tsx";
import CustomerRelationsManagement from "@Settings/components/AdministrationTools/CustomerRelationsManagement/CustomerRelationsManagement.tsx";
import { CRM_SECTION_IDENTIFIER } from "@Settings/Constants/AdministrationTools/AdministrationTools.ts";

const MemoizedGlobalPositioningService = memo(GlobalPositioningService);
const MemoizedKeyManagementSystem = memo(KeyManagementSystem);

type AdministrationToolsPartnerIntegrationsProps = {
	settings: IAdministrationTools;
	queryIsFinish: boolean;
	itemSelected: { elementId: string };
	classes: string;
	itemClick: (elementId: string, coordinate?: number) => void;
};

export default function AdministrationToolsPartnerIntegrations(props: Readonly<AdministrationToolsPartnerIntegrationsProps>) {
	const classes = props.classes;

	/***************************************** HANDLERS *****************************************/
	const handleToggleCollapse = (collapsed: boolean, expandedItemId: string) => {
		if (!collapsed) {
			props.itemClick(expandedItemId);
		}
	};

	return (
		<Grid sx={{ minHeight: "600px", width: "100%" }}>
			<Grid pl={"30px"}>
				{!props.queryIsFinish ? (
					<AdministrationToolsLoader />
				) : (
					<>
						<CollapseArea
							title="Payroll Provider"
							openCollapse={props.itemSelected?.elementId === "PayRollProvider"}
							collapsible
							onToggleCollapse={(collapsed) => handleToggleCollapse(collapsed, "PayRollProvider")}
						>
							<PayrollProvider
								isExpanded={props.itemSelected?.elementId === "PayRollProvider"}
								administrationTools={props.settings}
							/>
						</CollapseArea>
						<Divider sx={{ borderWidth: "1px", marginLeft: "-0" }} variant="middle" />
						<CollapseArea
							title="Dealer Management System"
							openCollapse={props.itemSelected?.elementId === "DealerManagementSystem"}
							collapsible
							onToggleCollapse={(collapsed) => handleToggleCollapse(collapsed, "DealerManagementSystem")}
						>
							<Stack display={"flex"} direction={"column"} mb={4}>
								<Stack
									display={"flex"}
									direction={"row"}
									justifyContent={"flex-start"}
									alignItems={"flex-start"}
									gap={4}
									mb={1}
								>
									<DealerManagementSystemProvider
										titles={["DMS Provider", "User ID Field"]}
										settings={props.settings}
										isExpanded={props.itemSelected?.elementId === "DealerManagementSystem"}
									/>
									<DealerManagementDataSource integrations={props.settings?.integrations ?? {}} />
								</Stack>
								<DealerManagementSystemIds integrations={props.settings?.integrations ?? {}} />
							</Stack>
							<Divider sx={{ borderWidth: "1px", marginLeft: "-0" }} variant="middle" />
							{/* Inventory Mapping */}
							<InventoryMapping integrations={props.settings?.integrations ?? []} />
							<Divider sx={{ borderWidth: "1px", marginLeft: "-0" }} variant="middle" />
							<DealerTradeAmountValues settings={props.settings} />
							<Divider sx={{ borderWidth: "1px", marginLeft: "-0" }} variant="middle" />
							<Stack display={"flex"} direction={"row"} gap={30}>
								<Sources settings={props.settings} />
								<CITControlTypes settings={props.settings} />
							</Stack>
							<Divider sx={{ borderWidth: "1px", marginTop: 4 }} />
							<GeneralLedgerPrefix title="General Ledger (GL) Prefix" settings={props.settings} />
						</CollapseArea>
						<Divider sx={{ borderWidth: "1px", marginLeft: "-0" }} variant="middle" />
						<CollapseArea
							title="Reputation Management"
							openCollapse={props.itemSelected?.elementId === "ReputationManagement"}
							collapsible
							onToggleCollapse={(collapsed) => handleToggleCollapse(collapsed, "ReputationManagement")}
						>
							<ReputationManagementSelection title="Provider" settings={props.settings} />
							<Divider sx={{ borderWidth: "1px", marginLeft: "-0" }} variant="middle" />
							<CommissionCycleLastUpdate title="Commission Cycle Last Update" settings={props.settings} />
						</CollapseArea>
						<Divider sx={{ borderWidth: "1px", marginLeft: "-0" }} variant="middle" />
						<CollapseArea
							title="Opportunity Mining"
							openCollapse={props.itemSelected?.elementId === "OpportunityMining"}
							collapsible
							onToggleCollapse={(collapsed) => handleToggleCollapse(collapsed, "OpportunityMining")}
						>
							<OpportunityMining classes={classes} settings={props.settings} title="Provider" />
						</CollapseArea>
						<Divider sx={{ borderWidth: "1px", marginLeft: "-0" }} variant="middle" />

						<CollapseArea
							title="Phone Service Provider"
							openCollapse={props.itemSelected?.elementId === "PhoneServiceProvider"}
							collapsible
							onToggleCollapse={(collapsed) => handleToggleCollapse(collapsed, "PhoneServiceProvider")}
						>
							<PhoneServiceProvider
								isExpanded={props.itemSelected?.elementId === "PhoneServiceProvider"}
								administrationTools={props.settings}
							/>
						</CollapseArea>
						<Divider sx={{ borderWidth: "1px", marginLeft: "-0" }} variant="middle" />

						<CollapseArea
							title="Global Positioning Service (GPS)"
							openCollapse={props.itemSelected?.elementId === "GlobalPositioningService"}
							collapsible
							onToggleCollapse={(collapsed) => handleToggleCollapse(collapsed, "GlobalPositioningService")}
						>
							<MemoizedGlobalPositioningService
								isExpanded={props.itemSelected?.elementId === "GlobalPositioningService"}
								administrationTools={props.settings}
							/>
						</CollapseArea>
						<Divider sx={{ borderWidth: "1px", marginLeft: "-0" }} variant="middle" />

						<CollapseArea
							title="Key Management System"
							openCollapse={props.itemSelected?.elementId === "KeyManagementSystem"}
							collapsible
							onToggleCollapse={(collapsed) => handleToggleCollapse(collapsed, "KeyManagementSystem")}
						>
							<MemoizedKeyManagementSystem
								isExpanded={props.itemSelected?.elementId === "KeyManagementSystem"}
								administrationTools={props.settings}
							/>
						</CollapseArea>

						<Divider sx={{ borderWidth: "1px", marginLeft: "-0" }} variant="middle" />

						<CollapseArea
							title="Customer Relationship Management (CRM)"
							openCollapse={props.itemSelected?.elementId === CRM_SECTION_IDENTIFIER}
							collapsible
							onToggleCollapse={(collapsed) => handleToggleCollapse(collapsed, CRM_SECTION_IDENTIFIER)}
						>
							<CustomerRelationsManagement
								isExpanded={props.itemSelected?.elementId === CRM_SECTION_IDENTIFIER}
								administrationTools={props.settings ?? {}}
							/>
						</CollapseArea>
					</>
				)}
			</Grid>
		</Grid>
	);
}
